import { useTranslation } from "react-i18next";
import { getLocale, translate as tr } from "../../i18n";
import { formatNumber } from "../../i18n/formatters";
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { Modal } from 'react-bootstrap';
import Sidebar from '../common/sidebar';
import Footer from '../common/footer';
import DataTable, { TableNote } from '../common/DataTable';
import '../../assets/css/leave.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return '--';
  return new Date(iso).toLocaleDateString(getLocale(), { month: 'short', day: 'numeric', year: 'numeric' });
}

const STATUS = {
  pending  : { label: 'Pending',   color: '#92400e', bg: '#fffbeb', border: '#fcd34d' },
  approved : { label: 'Approved',  color: '#166534', bg: '#f0fdf4', border: '#86efac' },
  rejected : { label: 'Rejected',  color: '#dc2626', bg: '#fef2f2', border: '#fca5a5' },
  cancelled: { label: 'Cancelled', color: '#6b7280', bg: '#f3f4f6', border: '#d1d5db' },
};

function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.pending;
  return (
    <span className="lv-badge" style={{ color: s.color, background: s.bg, border: `1px solid ${s.border}` }}>
      {tr(s.label)}
    </span>
  );
}

const COLORS = ['#4CAF50','#2196F3','#FF9800','#E91E63','#9C27B0','#00BCD4','#F44336','#607D8B'];

// ─── Component ────────────────────────────────────────────────────────────────

export default function LeavePage() {
  useTranslation();
  const auth         = useSelector((s) => s.auth);
  const isAdmin      = auth.is_workspace_admin === 1 || auth.workspace_role === 'ADMIN';
  const isPrivileged = isAdmin || auth.workspace_role === 'MANAGER';

  // Tab: 'mine' | 'approvals' | 'types' | 'balances'
  const [tab, setTab] = useState('mine');

  // Core data
  const [balances,    setBalances]    = useState([]);
  const [requests,    setRequests]    = useState([]);
  const [approvals,   setApprovals]   = useState([]);
  const [leaveTypes,  setLeaveTypes]  = useState([]);
  const [workflow,    setWorkflow]    = useState({ issues: [], audit: [], summary: {} });
  const [loading,     setLoading]     = useState(true);

  // ── New Request modal ──────────────────────────────────────────────────────
  const [reqOpen,       setReqOpen]       = useState(false);
  const [reqTypeId,     setReqTypeId]     = useState('');
  const [reqStart,      setReqStart]      = useState('');
  const [reqEnd,        setReqEnd]        = useState('');
  const [reqHalf,       setReqHalf]       = useState(false);
  const [reqHalfPeriod, setReqHalfPeriod] = useState('AM');
  const [reqNotes,      setReqNotes]      = useState('');
  const [reqError,      setReqError]      = useState('');
  const [reqSaving,     setReqSaving]     = useState(false);

  // ── Review modal ───────────────────────────────────────────────────────────
  const [reviewOpen,   setReviewOpen]   = useState(false);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [reviewAction, setReviewAction] = useState('');
  const [reviewNote,   setReviewNote]   = useState('');
  const [reviewSaving, setReviewSaving] = useState(false);
  const [reviewError,  setReviewError]  = useState('');

  // ── Leave Types admin modal ────────────────────────────────────────────────
  const [typeOpen,     setTypeOpen]     = useState(false);
  const [typeEdit,     setTypeEdit]     = useState(null); // null = create
  const [typeName,     setTypeName]     = useState('');
  const [typeColor,    setTypeColor]    = useState(COLORS[0]);
  const [typeDeducts,  setTypeDeducts]  = useState(true);
  const [typeApproval, setTypeApproval] = useState(true);
  const [typeApprovalScope, setTypeApprovalScope] = useState('WORKSPACE');
  const [typeApprovalLevels, setTypeApprovalLevels] = useState(1);
  const [typeApprovalTeamUserId, setTypeApprovalTeamUserId] = useState('');
  const [typeSick,     setTypeSick]     = useState(false);
  const [typeError,    setTypeError]    = useState('');
  const [typeSaving,   setTypeSaving]   = useState(false);

  // ── Balances admin tab ─────────────────────────────────────────────────────
  const [teamMembers,     setTeamMembers]     = useState([]);
  const [balUserId,       setBalUserId]       = useState('');
  const [balYear,         setBalYear]         = useState(new Date().getFullYear());
  const [balUserData,     setBalUserData]     = useState([]);
  const [balLoading,      setBalLoading]      = useState(false);
  const [balEditOpen,     setBalEditOpen]     = useState(false);
  const [balEditItem,     setBalEditItem]     = useState(null);
  const [balAllocated,    setBalAllocated]    = useState('');
  const [balSaving,       setBalSaving]       = useState(false);
  const [balError,        setBalError]        = useState('');

  const [toast, setToast] = useState('');

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${auth.token}`, 'Content-Type': 'application/json' },
  });

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  // ── Fetch core data ────────────────────────────────────────────────────────

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [balRes, reqRes, typeRes] = await Promise.all([
        axios.get(`/api/leave/balances?workspace_id=${auth.workspace_id}`, authHeaders()),
        axios.get(`/api/leave/requests?workspace_id=${auth.workspace_id}`, authHeaders()),
        axios.get(`/api/leave/types?workspace_id=${auth.workspace_id}`,    authHeaders()),
      ]);
      setBalances(balRes.data.results    || []);
      setRequests(reqRes.data.results    || []);
      setLeaveTypes(typeRes.data.results || []);

      if (isPrivileged) {
        const appRes = await axios.get(
          `/api/leave/requests?workspace_id=${auth.workspace_id}&status=pending`,
          authHeaders()
        );
        setApprovals(appRes.data.results || []);

        const workflowRes = await axios.get(
          `/api/workflow/overview?workspace_id=${auth.workspace_id}`,
          authHeaders()
        );
        setWorkflow({
          issues : workflowRes.data.issues || [],
          audit  : workflowRes.data.audit || [],
          summary: workflowRes.data.summary || {},
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const res = await axios.post('/api/team/list',
        JSON.stringify({ workspace_id: auth.workspace_id, is_active: 1 }),
        authHeaders()
      );
      setTeamMembers(res.data.results || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchAll(); }, [auth.workspace_id]);
  useEffect(() => {
    if (isAdmin && teamMembers.length === 0 && (tab === 'balances' || tab === 'types' || tab === 'workflow' || tab === 'approvals')) {
      fetchTeamMembers();
    }
  }, [tab, isAdmin, teamMembers.length]);

  // ── New Request ────────────────────────────────────────────────────────────

  const openNewRequest = () => {
    setReqTypeId(leaveTypes[0]?.id || '');
    setReqStart(''); setReqEnd(''); setReqHalf(false);
    setReqHalfPeriod('AM'); setReqNotes(''); setReqError('');
    setReqOpen(true);
  };

  const submitRequest = async () => {
    if (!reqTypeId || !reqStart || (!reqHalf && !reqEnd)) { setReqError('All fields are required'); return; }
    setReqSaving(true); setReqError('');
    try {
      await axios.post('/api/leave/requests', JSON.stringify({
        workspace_id   : auth.workspace_id,
        leave_type_id  : reqTypeId,
        start_date     : reqStart,
        end_date       : reqHalf ? reqStart : reqEnd,
        is_half_day    : reqHalf,
        half_day_period: reqHalf ? reqHalfPeriod : undefined,
        notes          : reqNotes || undefined,
      }), authHeaders());
      setReqOpen(false);
      showToast('Leave request submitted');
      fetchAll();
    } catch (err) {
      setReqError(err.response?.data?.errors?.[0]?.msg || tr("Failed to submit"));
    } finally {
      setReqSaving(false);
    }
  };

  const cancelRequest = async (id) => {
    try {
      await axios.put(`/api/leave/requests/${id}/cancel`,
        JSON.stringify({ workspace_id: auth.workspace_id }), authHeaders());
      showToast('Request cancelled');
      fetchAll();
    } catch (err) {
      showToast(err.response?.data?.errors?.[0]?.msg || tr("Error cancelling"));
    }
  };

  // ── Review ─────────────────────────────────────────────────────────────────

  const openReview = (req, action) => {
    setReviewTarget(req); setReviewAction(action);
    setReviewNote(''); setReviewError(''); setReviewOpen(true);
  };

  const submitReview = async () => {
    setReviewSaving(true); setReviewError('');
    try {
      const res = await axios.post(`/api/leave/requests/${reviewTarget.id}/${reviewAction}`,
        JSON.stringify({ workspace_id: auth.workspace_id, review_note: reviewNote }),
        authHeaders());
      setReviewOpen(false);
      if (reviewAction === 'approve' && res.data?.approval_pending) {
        showToast(`Approval step saved. Next step ${res.data.next_step || ''} still pending.`);
      } else {
        showToast(`Request ${reviewAction === 'approve' ? 'approved' : 'rejected'}`);
      }
      fetchAll();
    } catch (err) {
      setReviewError(err.response?.data?.errors?.[0]?.msg || tr("Error"));
    } finally {
      setReviewSaving(false);
    }
  };

  const resolveWorkflowIssue = async (issue) => {
    try {
      await axios.patch(
        `/api/workflow/issues/${issue.id}/resolve`,
        JSON.stringify({
          workspace_id: auth.workspace_id,
          resolution_note: `Resolved from Leave page by ${auth.email || 'manager'}`,
        }),
        authHeaders()
      );
      showToast('Issue resolved');
      fetchAll();
    } catch (err) {
      showToast(err.response?.data?.errors?.[0]?.msg || tr("Error resolving issue"));
    }
  };

  // ── Leave Types CRUD ───────────────────────────────────────────────────────

  const openTypeCreate = () => {
    setTypeEdit(null); setTypeName(''); setTypeColor(COLORS[0]);
    setTypeDeducts(true); setTypeApproval(true); setTypeSick(false); setTypeError('');
    setTypeApprovalScope('WORKSPACE'); setTypeApprovalLevels(1); setTypeApprovalTeamUserId('');
    setTypeOpen(true);
  };

  const openTypeEdit = (t) => {
    setTypeEdit(t); setTypeName(t.name); setTypeColor(t.color || COLORS[0]);
    setTypeDeducts(t.deducts_balance); setTypeApproval(t.requires_approval);
    setTypeApprovalScope(t.approval_scope || 'WORKSPACE');
    setTypeApprovalLevels(t.approval_levels || 1);
    setTypeApprovalTeamUserId(t.approval_team_user_id || '');
    setTypeSick(t.is_sick_leave || false);
    setTypeError(''); setTypeOpen(true);
  };

  const saveType = async () => {
    if (!typeName.trim()) { setTypeError('Name is required'); return; }
    setTypeSaving(true); setTypeError('');
    try {
      if (typeEdit) {
        await axios.put(`/api/leave/types/${typeEdit.id}`, JSON.stringify({
          workspace_id     : auth.workspace_id,
          name             : typeName,
          color            : typeColor,
          deducts_balance  : typeDeducts,
          requires_approval: typeApproval,
          approval_scope   : typeApprovalScope,
          approval_levels  : typeApprovalLevels,
          approval_team_user_id: typeApprovalTeamUserId || null,
          is_sick_leave    : typeSick,
        }), authHeaders());
        showToast('Leave type updated');
      } else {
        await axios.post('/api/leave/types', JSON.stringify({
          workspace_id     : auth.workspace_id,
          name             : typeName,
          color            : typeColor,
          deducts_balance  : typeDeducts,
          requires_approval: typeApproval,
          approval_scope   : typeApprovalScope,
          approval_levels  : typeApprovalLevels,
          approval_team_user_id: typeApprovalTeamUserId || null,
          is_sick_leave    : typeSick,
        }), authHeaders());
        showToast('Leave type created');
      }
      setTypeOpen(false);
      fetchAll();
    } catch (err) {
      setTypeError(err.response?.data?.errors?.[0]?.msg || tr("Error saving"));
    } finally {
      setTypeSaving(false);
    }
  };

  const toggleTypeActive = async (t) => {
    try {
      await axios.put(`/api/leave/types/${t.id}`, JSON.stringify({
        workspace_id: auth.workspace_id,
        is_active   : !t.is_active,
      }), authHeaders());
      fetchAll();
    } catch (err) {
      showToast('Error toggling type');
    }
  };

  // ── Balances admin ─────────────────────────────────────────────────────────

  const fetchUserBalances = async (userId, year) => {
    if (!userId) return;
    setBalLoading(true);
    try {
      const res = await axios.get(
        `/api/leave/balances?workspace_id=${auth.workspace_id}&year=${year}&user_id=${userId}`,
        authHeaders()
      );
      setBalUserData(res.data.results || []);
    } catch (err) {
      console.error(err);
    } finally {
      setBalLoading(false);
    }
  };

  const handleBalUserChange = (uid) => {
    setBalUserId(uid);
    setBalUserData([]);
    if (uid) fetchUserBalances(uid, balYear);
  };

  const handleBalYearChange = (y) => {
    setBalYear(y);
    setBalUserData([]);
    if (balUserId) fetchUserBalances(balUserId, y);
  };

  const openBalEdit = (item) => {
    setBalEditItem(item);
    setBalAllocated(item.allocated_days ?? '');
    setBalError('');
    setBalEditOpen(true);
  };

  const saveBalance = async () => {
    if (balAllocated === '' || isNaN(parseFloat(balAllocated))) {
      setBalError('Enter a valid number of days');
      return;
    }
    setBalSaving(true); setBalError('');
    try {
      await axios.post('/api/leave/balances', JSON.stringify({
        workspace_id  : auth.workspace_id,
        user_id       : balUserId,
        leave_type_id : balEditItem.leave_type_id,
        year          : balYear,
        allocated_days: parseFloat(balAllocated),
      }), authHeaders());
      setBalEditOpen(false);
      showToast('Balance saved');
      fetchUserBalances(balUserId, balYear);
    } catch (err) {
      setBalError(err.response?.data?.errors?.[0]?.msg || tr("Error saving"));
    } finally {
      setBalSaving(false);
    }
  };

  const yearOptions = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear - 2; y <= currentYear + 1; y++) yearOptions.push(y);

  const requestColumns = (review = false) => [
    ...(review ? [{ id: 'employee', header: tr("Employee"), accessorFn: r => `${r.user?.first_name || ''} ${r.user?.last_name || ''} ${r.user?.email || ''}`, size: 200, cell: ({ row: { original: r } }) => <><div className="lv-emp-name">{r.user?.first_name} {r.user?.last_name}</div><div className="lv-emp-email">{r.user?.email}</div></> }] : []),
    { id: 'type', header: tr("Type"), accessorFn: r => r.leave_type?.name || '', size: 145, cell: ({ row: { original: r } }) => <><span className="lv-type-dot" style={{ background: r.leave_type?.color }} />{r.leave_type?.name}</> },
    { accessorKey: 'start_date', header: tr("Dates"), size: 265, cell: ({ row: { original: r } }) => <>{formatDate(r.start_date)}{r.start_date !== r.end_date && <> → {formatDate(r.end_date)}</>}{r.is_half_day && r.half_day_period && <span className="lv-half-badge">{r.half_day_period}</span>}</> },
    { accessorKey: 'duration_days', header: tr("Duration"), size: 105, meta: { className: 'mz-table-nowrap' }, sortingFn: (a, b) => Number(a.original.duration_days) - Number(b.original.duration_days), cell: ({ getValue }) => tr('leaveDuration', { count: Number(getValue()), days: formatNumber(getValue()) }) },
    ...(!review ? [{ accessorKey: 'status', header: tr("Status"), size: 125, filterFn: 'equalsString', cell: ({ getValue }) => <StatusBadge status={getValue()} /> }] : []),
    { accessorKey: 'notes', header: tr("Notes"), size: 260, cell: ({ getValue }) => <TableNote value={getValue()} /> },
    { id: 'actions', header: tr('tableActions'), size: review ? 210 : 120, enableSorting: false, cell: ({ row: { original: r } }) => <div className="mz-table-actions">{review ? <><button className="lv-approve-btn" onClick={() => openReview(r, 'approve')}>{tr("Approve")}</button><button className="lv-reject-btn" onClick={() => openReview(r, 'reject')}>{tr("Reject")}</button></> : r.status === 'pending' && <button className="lv-cancel-btn" onClick={() => cancelRequest(r.id)}>{tr("Cancel")}</button>}</div> },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div>
      <Sidebar />
      <div className={`lv-page lv-page--${tab}`}>

        {/* Header */}
        <div className="lv-header">
          <div>
            <h2 className="lv-title">{tr("Leave")}</h2>
            <p className="lv-subtitle">{tr("Manage time off and leave requests.")}</p>
          </div>
          <button className="lv-btn-primary" onClick={openNewRequest}>{tr("+ New Request")}</button>
        </div>

        {toast && <div className="lv-toast">{toast}</div>}

        {/* Balance cards — own balances, always visible */}
        {balances.length > 0 && (
          <div className="lv-balance-grid">
            {balances.map((b) => (
              <div key={b.leave_type_id} className="lv-balance-card">
                <div className="lv-balance-dot" style={{ background: b.leave_type_color }} />
                <div className="lv-balance-type">{b.leave_type_name}</div>
                {b.deducts_balance ? (
                  <>
                    <div className="lv-balance-main">{formatNumber(b.remaining_days)}<span> / {formatNumber(b.allocated_days)} {tr("d")}</span></div>
                    <div className="lv-balance-sub">
                      {tr("leaveBalanceUsed", { count: Number(b.used_days), days: formatNumber(b.used_days) })}{b.pending_days > 0 && <> · {tr("leaveBalancePending", { days: formatNumber(b.pending_days) })}</> }
                    </div>
                  </>
                ) : (
                  <div className="lv-balance-main lv-balance-main--no-limit">{tr("No limit")}</div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="lv-tabs" aria-label={tr("Leave")}>
          <button className={`lv-tab${tab === 'mine' ? ' lv-tab--active' : ''}`} onClick={() => setTab('mine')}>{tr("My Requests")} </button>
          {isPrivileged && (
            <button className={`lv-tab${tab === 'approvals' ? ' lv-tab--active' : ''}`} onClick={() => setTab('approvals')}>{tr("Pending Approvals")} {approvals.length > 0 && <span className="lv-tab-badge">{approvals.length}</span>}
            </button>
          )}
          {isAdmin && (
            <button className={`lv-tab${tab === 'types' ? ' lv-tab--active' : ''}`} onClick={() => setTab('types')}>{tr("Leave Types")} </button>
          )}
          {isAdmin && (
            <button className={`lv-tab${tab === 'balances' ? ' lv-tab--active' : ''}`} onClick={() => setTab('balances')}>{tr("Balances")} </button>
          )}
          {isPrivileged && (
            <button className={`lv-tab${tab === 'workflow' ? ' lv-tab--active' : ''}`} onClick={() => setTab('workflow')}>{tr("Workflow")} {workflow.summary?.open_issues > 0 && <span className="lv-tab-badge">{workflow.summary.open_issues}</span>}
            </button>
          )}
        </div>

        {tab === 'mine' && (loading ? <div className="lv-empty">{tr("Loading...")}</div> :
          <DataTable key="mine" data={requests} columns={requestColumns()} label={tr("My Requests")} initialSorting={[{ id: 'start_date', desc: true }]} filters={[{ id: 'status', label: tr('tableAllStatuses'), options: Object.entries(STATUS).map(([value, status]) => ({ value, label: tr(status.label) })) }]} />
        )}
        {tab === 'approvals' && isPrivileged && (loading ? <div className="lv-empty">{tr("Loading...")}</div> :
          <DataTable key="approvals" data={approvals} columns={requestColumns(true)} label={tr("Pending Approvals")} initialSorting={[{ id: 'start_date', desc: false }]} />
        )}

        {/* ── Leave Types (Admin) ── */}
        {tab === 'types' && isAdmin && (
          <div>
            <div className="lv-admin-toolbar">
              <span className="lv-admin-toolbar__count">{leaveTypes.length} {tr("type")}{leaveTypes.length !== 1 ? 's' : ''}</span>
              <button className="lv-btn-primary" onClick={openTypeCreate}>{tr("+ New Type")}</button>
            </div>
            <div className="lv-table-wrap">
              <div className="lv-row lv-row--header lv-row--types">
                <div className="lv-cell">{tr("Name")}</div>
                <div className="lv-cell">{tr("Deducts Balance")}</div>
                <div className="lv-cell">{tr("Requires Approval")}</div>
                <div className="lv-cell">{tr("Status")}</div>
                <div className="lv-cell" />
              </div>
              {leaveTypes.map((t) => (
                <div key={t.id} className="lv-row lv-row--data lv-row--types">
                  <div className="lv-cell" data-label={tr("Name")} >
                    <span className="lv-type-dot" style={{ background: t.color }} />
                    {t.name}
                  </div>
                  <div className="lv-cell" data-label={tr("Deducts Balance")} >
                    <span className={`lv-pill ${t.deducts_balance ? 'lv-pill--yes' : 'lv-pill--no'}`}>
                      {t.deducts_balance ? tr("Yes") : tr("No")}
                    </span>
                  </div>
                  <div className="lv-cell" data-label={tr("Requires Approval")} >
                    <span className={`lv-pill ${t.requires_approval ? 'lv-pill--yes' : 'lv-pill--no'}`}>
                      {t.requires_approval ? tr("Yes") : tr("No")}
                    </span>
                  </div>
                  <div className="lv-cell" data-label={tr("Status")} >
                    <button
                      className={`lv-toggle-btn ${t.is_active ? 'lv-toggle-btn--on' : 'lv-toggle-btn--off'}`}
                      onClick={() => toggleTypeActive(t)}
                    >
                      {t.is_active ? tr("Active") : tr("Inactive")}
                    </button>
                  </div>
                  <div className="lv-cell lv-cell--actions" >
                    <button className="lv-edit-btn" onClick={() => openTypeEdit(t)}>{tr("Edit")}</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Balances (Admin) ── */}
        {tab === 'balances' && isAdmin && (
          <div>
            <div className="lv-bal-controls">
              <div className="lv-bal-control-group">
                <label className="lv-bal-label">{tr("Team Member")}</label>
                <select
                  className="lv-bal-select"
                  value={balUserId}
                  onChange={(e) => handleBalUserChange(e.target.value)}
                >
                  <option value="">{tr("— Select member —")}</option>
                  {teamMembers.map((m) => (
                    <option key={m.user_id} value={m.user_id}>
                      {m.first_name} {m.last_name} ({m.email})
                    </option>
                  ))}
                </select>
              </div>
              <div className="lv-bal-control-group">
                <label className="lv-bal-label">{tr("Year")}</label>
                <select
                  className="lv-bal-select lv-bal-select--year"
                  value={balYear}
                  onChange={(e) => handleBalYearChange(parseInt(e.target.value))}
                >
                  {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>

            {!balUserId && (
              <div className="lv-empty">{tr("Select a team member to view and edit their leave balances.")}</div>
            )}

            {balUserId && balLoading && <div className="lv-empty">{tr("Loading...")}</div>}

            {balUserId && !balLoading && balUserData.length > 0 && (
              <div className="lv-table-wrap">
                <div className="lv-row lv-row--header lv-row--bal">
                  <div className="lv-cell">{tr("Leave Type")}</div>
                  <div className="lv-cell">{tr("Allocated")}</div>
                  <div className="lv-cell">{tr("Used")}</div>
                  <div className="lv-cell">{tr("Pending")}</div>
                  <div className="lv-cell">{tr("Remaining")}</div>
                  <div className="lv-cell" />
                </div>
                {balUserData.map((b) => (
                  <div key={b.leave_type_id} className="lv-row lv-row--data lv-row--bal">
                    <div className="lv-cell" data-label={tr("Leave Type")} >
                      <span className="lv-type-dot" style={{ background: b.leave_type_color }} />
                      {b.leave_type_name}
                    </div>
                    <div className="lv-cell lv-cell--num" data-label={tr("Allocated")} >{b.allocated_days}{tr("d")}</div>
                    <div className="lv-cell lv-cell--num" data-label={tr("Used")} >{b.used_days}{tr("d")}</div>
                    <div className="lv-cell lv-cell--num" data-label={tr("Pending")} >{b.pending_days}{tr("d")}</div>
                    <div className="lv-cell lv-cell--num" data-label={tr("Remaining")} >
                      <span style={{ color: b.remaining_days > 0 ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                        {b.remaining_days}{tr("d")} </span>
                    </div>
                    <div className="lv-cell lv-cell--actions" >
                      {b.deducts_balance && (
                        <button className="lv-edit-btn" onClick={() => openBalEdit(b)}>{tr("Set Allocation")} </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Workflow ── */}
        {tab === 'workflow' && isPrivileged && (
          <div>
            <div className="lv-workflow-grid">
              <div className="lv-workflow-card">
                <div className="lv-workflow-card__label">{tr("Open issues")}</div>
                <div className="lv-workflow-card__value">{workflow.summary?.open_issues || 0}</div>
                <div className="lv-workflow-card__meta">
                  {workflow.summary?.critical_issues || 0} {tr("critical ·")} {workflow.summary?.warning_issues || 0} {tr("warnings")} </div>
              </div>
              <div className="lv-workflow-card">
                <div className="lv-workflow-card__label">{tr("Audit events")}</div>
                <div className="lv-workflow-card__value">{workflow.summary?.audit_events || 0}</div>
                <div className="lv-workflow-card__meta">{tr("Recent approvals, fixes, and resolutions")}</div>
              </div>
            </div>

            <div className="lv-workflow-section">
              <div className="lv-workflow-section__header">
                <h3>{tr("Open Issues")}</h3>
                <span>{workflow.issues.length} {tr("unresolved")}</span>
              </div>
              {workflow.issues.length === 0 ? (
                <div className="lv-empty" style={{ marginTop: 0 }}>{tr("No open workflow issues.")}</div>
              ) : (
                <div className="lv-table-wrap">
                  <div className="lv-row lv-row--header">
                    <div className="lv-cell">{tr("Issue")}</div>
                    <div className="lv-cell">{tr("Employee")}</div>
                    <div className="lv-cell">{tr("Reason")}</div>
                    <div className="lv-cell">{tr("Due")}</div>
                    <div className="lv-cell" />
                  </div>
                  {workflow.issues.map((issue) => (
                    <div className="lv-row lv-row--data" key={issue.id}>
                      <div className="lv-cell" data-label={tr("Issue")} >
                        <strong>{issue.title}</strong>
                        <div className="lv-emp-email">{issue.issue_type}</div>
                      </div>
                      <div className="lv-cell" data-label={tr("Employee")} >
                        <div className="lv-emp-name">{issue.user?.first_name} {issue.user?.last_name}</div>
                        <div className="lv-emp-email">{issue.user?.email}</div>
                      </div>
                      <div className="lv-cell lv-cell--notes" data-label={tr("Reason")} >{issue.reason}</div>
                      <div className="lv-cell" data-label={tr("Due")} >{issue.due_at ? formatDate(issue.due_at) : '--'}</div>
                      <div className="lv-cell lv-cell--actions" >
                        <button className="lv-edit-btn" onClick={() => resolveWorkflowIssue(issue)}>{tr("Resolve")}</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="lv-workflow-section">
              <div className="lv-workflow-section__header">
                <h3>{tr("Recent Activity")}</h3>
                <span>{workflow.audit.length} {tr("latest events")}</span>
              </div>
              {workflow.audit.length === 0 ? (
                <div className="lv-empty" style={{ marginTop: 0 }}>{tr("No workflow activity yet.")}</div>
              ) : (
                <div className="lv-activity-list">
                  {workflow.audit.map((item) => (
                    <div className="lv-activity-item" key={item.id}>
                      <div className="lv-activity-main">
                        <strong>{item.action_type}</strong>
                        <span>{item.entity_type} #{item.entity_id || '--'}</span>
                      </div>
                      <div className="lv-activity-meta">
                        <span>{item.actor ? `${item.actor.first_name || ''} ${item.actor.last_name || ''}`.trim() || item.actor.email : tr("System")}</span>
                        <span>{formatDate(item.created_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

      </div>
      <Footer />

      {/* ── New Request Modal ── */}
      <Modal show={reqOpen} onHide={() => setReqOpen(false)} dialogClassName="add-New-tag-popup-box-container" bsPrefix="">
        <div className="pm-header" style={{ background: '#1a5c30' }}>
          <h3 className="pm-title">{tr("New Leave Request")}</h3>
          <button className="pm-close" onClick={() => setReqOpen(false)}>x</button>
        </div>
        <div className="pm-body">
          <div className="pm-field">
            <label className="pm-label">{tr("Leave Type")} <span style={{ color: '#dc2626' }}>*</span></label>
            <select className="pm-input" value={reqTypeId} onChange={(e) => setReqTypeId(e.target.value)}>
              {leaveTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>

          <div className="pm-field">
            <label className="pm-label">
              <input type="checkbox" checked={reqHalf} onChange={(e) => setReqHalf(e.target.checked)} style={{ marginRight: 6 }} />{tr("Half day")} </label>
            {reqHalf && (
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                {['AM', 'PM'].map((p) => (
                  <button key={p}
                    className={`tm-type-toggle-btn${reqHalfPeriod === p ? ' tm-type-toggle-btn--active' : ''}`}
                    onClick={() => setReqHalfPeriod(p)}
                  >{p}</button>
                ))}
              </div>
            )}
          </div>

          <div className="pm-field">
            <label className="pm-label">{reqHalf ? tr("Date") : tr("Start Date")} <span style={{ color: '#dc2626' }}>*</span></label>
            <input className="pm-input" type="date" value={reqStart} onChange={(e) => setReqStart(e.target.value)} />
          </div>

          {!reqHalf && (
            <div className="pm-field">
              <label className="pm-label">{tr("End Date")} <span style={{ color: '#dc2626' }}>*</span></label>
              <input className="pm-input" type="date" value={reqEnd} min={reqStart} onChange={(e) => setReqEnd(e.target.value)} />
            </div>
          )}

          <div className="pm-field">
            <label className="pm-label">{tr("Notes")} <span className="pm-label-hint">{tr("(optional)")}</span></label>
            <input className="pm-input" type="text" placeholder={tr("Reason or additional info...")} value={reqNotes} onChange={(e) => setReqNotes(e.target.value)} />
          </div>

          {reqError && <div className="att-modal-error">{reqError}</div>}
        </div>
        <div className="pm-footer">
          <button className="pm-btn-cancel" onClick={() => setReqOpen(false)}>{tr("Cancel")}</button>
          <button className="pm-btn-create" onClick={submitRequest} disabled={reqSaving}>
            {reqSaving ? tr("Submitting...") : tr("Submit Request")}
          </button>
        </div>
      </Modal>

      {/* ── Review Modal ── */}
      <Modal show={reviewOpen} onHide={() => setReviewOpen(false)} dialogClassName="add-New-tag-popup-box-container" bsPrefix="">
        <div className="pm-header" style={{ background: reviewAction === 'approve' ? '#1a5c30' : '#dc2626' }}>
          <h3 className="pm-title">{reviewAction === 'approve' ? tr("Approve") : tr("Reject")} {tr("Request")}</h3>
          <button className="pm-close" onClick={() => setReviewOpen(false)}>x</button>
        </div>
        {reviewTarget && (
          <div className="pm-body">
            <div className="att-modal-meta">
              <strong>{reviewTarget.user?.first_name} {reviewTarget.user?.last_name}</strong>
              <span>{reviewTarget.leave_type?.name}</span>
              <span>{formatDate(reviewTarget.start_date)}{reviewTarget.start_date !== reviewTarget.end_date && ` - ${formatDate(reviewTarget.end_date)}`}</span>
              <span>{reviewTarget.duration_days}{tr("d")}</span>
            </div>
            <div className="pm-field">
              <label className="pm-label">{tr("Note")} <span className="pm-label-hint">{tr("(optional)")}</span></label>
              <input className="pm-input" type="text" placeholder={tr("Add a note for the employee...")} value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} />
            </div>
            {reviewError && <div className="att-modal-error">{reviewError}</div>}
          </div>
        )}
        <div className="pm-footer">
          <button className="pm-btn-cancel" onClick={() => setReviewOpen(false)}>{tr("Cancel")}</button>
          <button
            onClick={submitReview}
            disabled={reviewSaving}
            style={{ padding: '8px 18px', borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, background: reviewAction === 'approve' ? '#16a34a' : '#dc2626', color: '#fff' }}
          >
            {reviewSaving ? '...' : reviewAction === 'approve' ? tr("Approve") : tr("Reject")}
          </button>
        </div>
      </Modal>

      {/* ── Leave Type Modal (Create / Edit) ── */}
      <Modal show={typeOpen} onHide={() => setTypeOpen(false)} dialogClassName="add-New-tag-popup-box-container" bsPrefix="">
        <div className="pm-header" style={{ background: '#1a5c30' }}>
          <h3 className="pm-title">{typeEdit ? tr("Edit Leave Type") : tr("New Leave Type")}</h3>
          <button className="pm-close" onClick={() => setTypeOpen(false)}>x</button>
        </div>
        <div className="pm-body">
          <div className="pm-field">
            <label className="pm-label">{tr("Name")} <span style={{ color: '#dc2626' }}>*</span></label>
            <input className="pm-input" type="text" placeholder={tr("e.g. Annual Leave")} value={typeName} onChange={(e) => setTypeName(e.target.value)} />
          </div>

          <div className="pm-field">
            <label className="pm-label">{tr("Color")}</label>
            <div className="lv-color-picker">
              {COLORS.map((c) => (
                <button
                  key={c}
                  className={`lv-color-swatch${typeColor === c ? ' lv-color-swatch--active' : ''}`}
                  style={{ background: c }}
                  onClick={() => setTypeColor(c)}
                />
              ))}
            </div>
          </div>

          <div className="pm-field">
            <label className="pm-label">{tr("Options")}</label>
            <div className="lv-type-options">
              <label className="lv-type-option">
                <input type="checkbox" checked={typeDeducts} onChange={(e) => setTypeDeducts(e.target.checked)} />
                <span>{tr("Deducts from balance")}</span>
                <small>{tr("Counts against allocated days")}</small>
              </label>
              <label className="lv-type-option">
                <input type="checkbox" checked={typeApproval} onChange={(e) => setTypeApproval(e.target.checked)} />
                <span>{tr("Requires approval")}</span>
                <small>{tr("Request stays pending until approved")}</small>
              </label>
              <label className="lv-type-option">
                <input type="checkbox" checked={typeSick} onChange={(e) => setTypeSick(e.target.checked)} />
                <span>{tr("Sick leave (EFZG)")}</span>
                <small>{tr("Tracks the 42-day Entgeltfortzahlung limit")}</small>
              </label>
            </div>
          </div>

          <div className="pm-field">
            <label className="pm-label">{tr("Approval scope")}</label>
            <select className="pm-input" value={typeApprovalScope} onChange={(e) => setTypeApprovalScope(e.target.value)}>
              <option value="WORKSPACE">{tr("Workspace default")}</option>
              <option value="TEAM">{tr("Team lead first")}</option>
              <option value="MULTI_LEVEL">{tr("Multi-level approval")}</option>
            </select>
            <div className="pm-label-hint" style={{ display: 'block', marginTop: 6 }}>{tr("Workspace default uses the standard manager/admin review. Team lead first routes the first approval to one designated approver.")} </div>
          </div>

          <div className="pm-field">
            <label className="pm-label">{tr("Approval levels")}</label>
            <input
              className="pm-input"
              type="number"
              min="1"
              max="3"
              value={typeApprovalLevels}
              onChange={(e) => setTypeApprovalLevels(parseInt(e.target.value, 10) || 1)}
            />
          </div>

          <div className="pm-field">
            <label className="pm-label">{tr("Team lead approver")} <span className="pm-label-hint">{tr("(optional)")}</span></label>
            <select className="pm-input" value={typeApprovalTeamUserId} onChange={(e) => setTypeApprovalTeamUserId(e.target.value)}>
              <option value="">{tr("Use manager/admin fallback")}</option>
              {teamMembers.map((m) => (
                <option key={m.user_id} value={m.user_id}>
                  {m.first_name} {m.last_name} ({m.email})
                </option>
              ))}
            </select>
          </div>

          {typeError && <div className="att-modal-error">{typeError}</div>}
        </div>
        <div className="pm-footer">
          <button className="pm-btn-cancel" onClick={() => setTypeOpen(false)}>{tr("Cancel")}</button>
          <button className="pm-btn-create" onClick={saveType} disabled={typeSaving}>
            {typeSaving ? tr("Saving...") : typeEdit ? tr("Save Changes") : tr("Create Type")}
          </button>
        </div>
      </Modal>

      {/* ── Set Balance Modal ── */}
      <Modal show={balEditOpen} onHide={() => setBalEditOpen(false)} dialogClassName="add-New-tag-popup-box-container" bsPrefix="">
        <div className="pm-header" style={{ background: '#1a5c30' }}>
          <h3 className="pm-title">{tr("Set Allocation —")} {balEditItem?.leave_type_name}</h3>
          <button className="pm-close" onClick={() => setBalEditOpen(false)}>x</button>
        </div>
        <div className="pm-body">
          {balEditItem && (
            <div className="lv-bal-summary">
              <div className="lv-bal-summary__row">
                <span>{tr("Used")}</span><strong>{balEditItem.used_days}{tr("d")}</strong>
              </div>
              <div className="lv-bal-summary__row">
                <span>{tr("Pending")}</span><strong>{balEditItem.pending_days}{tr("d")}</strong>
              </div>
            </div>
          )}
          <div className="pm-field">
            <label className="pm-label">{tr("Allocated Days")} <span style={{ color: '#dc2626' }}>*</span></label>
            <input
              className="pm-input"
              type="number"
              min="0"
              step="0.5"
              placeholder={tr("e.g. 20")}
              value={balAllocated}
              onChange={(e) => setBalAllocated(e.target.value)}
            />
          </div>
          {balError && <div className="att-modal-error">{balError}</div>}
        </div>
        <div className="pm-footer">
          <button className="pm-btn-cancel" onClick={() => setBalEditOpen(false)}>{tr("Cancel")}</button>
          <button className="pm-btn-create" onClick={saveBalance} disabled={balSaving}>
            {balSaving ? tr("Saving...") : tr("Save")}
          </button>
        </div>
      </Modal>
    </div>
  );
}
